/**
 * useContentSelectionModal Hook
 *
 * Manages selection state for the Content Selection Modal.
 * Handles item selection, filtering, search, and pagination for
 * selective schema processing.
 *
 * Based on selection patterns from ClusterResultsPage.tsx
 */

import { useState, useMemo, useCallback } from "react";
import type { ContentItem, ContentFilters } from "@/types/schema";

export interface UseContentSelectionModalOptions {
	/**
	 * Initial page size for pagination
	 * @default 50
	 */
	pageSize?: number;
}

export interface UseContentSelectionModalReturn {
	// Data
	items: ContentItem[];
	filteredItems: ContentItem[];

	// Selection state
	selectedIds: number[];
	selectedCount: number;

	// Filter state
	filters: ContentFilters;
	searchTerm: string;

	// Pagination state
	page: number;
	pageSize: number;

	// Selection methods
	toggleSelection: (id: number) => void;
	selectAllPage: (currentPageItems: ContentItem[]) => void;
	selectAllFiltered: (allFilteredItems: ContentItem[]) => void;
	clearSelection: () => void;
	setSelectedIds: React.Dispatch<React.SetStateAction<number[]>>;

	// Filter methods
	setFilters: React.Dispatch<React.SetStateAction<ContentFilters>>;
	setSearchTerm: (term: string) => void;

	// Pagination methods
	setPage: (page: number) => void;
	setPageSize: (size: number) => void;
}

/**
 * Hook to manage content selection modal state
 *
 * Provides centralized state management for:
 * - Item selection (checkboxes)
 * - Filtering by schema status and content type
 * - Search by title/URL
 * - Pagination
 *
 * @param initialItems - Initial list of content items (can be empty)
 * @param options - Configuration options
 */
export function useContentSelectionModal(
	initialItems: ContentItem[] = [],
	options: UseContentSelectionModalOptions = {},
): UseContentSelectionModalReturn {
	const { pageSize: initialPageSize = 50 } = options;

	// Selection state
	const [selectedIds, setSelectedIds] = useState<number[]>([]);

	// Filter state
	const [filters, setFilters] = useState<ContentFilters>({
		content_type: "all",
		schema_status: "all",
	});
	const [searchTerm, setSearchTerm] = useState("");

	// Pagination state
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(initialPageSize);

	// Items (will be updated externally via React Query)
	const [items] = useState<ContentItem[]>(initialItems);

	// Computed: filtered items based on search term
	const filteredItems = useMemo(() => {
		if (!searchTerm) return items;

		const term = searchTerm.toLowerCase();
		return items.filter(
			(item) =>
				item.title.toLowerCase().includes(term) ||
				item.slug.toLowerCase().includes(term) ||
				item.url?.toLowerCase().includes(term),
		);
	}, [items, searchTerm]);

	// Computed: selected count
	const selectedCount = selectedIds.length;

	// Selection methods

	/**
	 * Toggle selection of a single item
	 */
	const toggleSelection = useCallback((id: number) => {
		setSelectedIds((prev) =>
			prev.includes(id)
				? prev.filter((itemId) => itemId !== id)
				: [...prev, id],
		);
	}, []);

	/**
	 * Select all items on the current page
	 */
	const selectAllPage = useCallback((currentPageItems: ContentItem[]) => {
		const pageIds = currentPageItems.map((item) => item.id);
		setSelectedIds((prev) => {
			// Add page IDs that aren't already selected
			const newIds = pageIds.filter((id) => !prev.includes(id));
			return [...prev, ...newIds];
		});
	}, []);

	/**
	 * Select all filtered items (respects current filters/search)
	 */
	const selectAllFiltered = useCallback((allFilteredItems: ContentItem[]) => {
		const allIds = allFilteredItems.map((item) => item.id);
		setSelectedIds((prev) => {
			// Add filtered IDs that aren't already selected
			const newIds = allIds.filter((id) => !prev.includes(id));
			return [...prev, ...newIds];
		});
	}, []);

	/**
	 * Clear all selections
	 */
	const clearSelection = useCallback(() => {
		setSelectedIds([]);
	}, []);

	return {
		// Data
		items,
		filteredItems,

		// Selection state
		selectedIds,
		selectedCount,

		// Filter state
		filters,
		searchTerm,

		// Pagination state
		page,
		pageSize,

		// Selection methods
		toggleSelection,
		selectAllPage,
		selectAllFiltered,
		clearSelection,
		setSelectedIds,

		// Filter methods
		setFilters,
		setSearchTerm,

		// Pagination methods
		setPage,
		setPageSize,
	};
}
